import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/vendly';
import styles from './NotificationBell.module.css';

const TYPE_ICON = {
  order:   '📦',
  payment: '💸',
  seller:  '🏪',
  info:    'ℹ️',
};

export default function NotificationBell() {
  const [notifs,  setNotifs]  = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = () => {
    api.get('/notifications')
      .then(res => {
        setNotifs(res.data.notifications || []);
        setUnread(res.data.unread || 0);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchNotifs();
    // Poll every 30s
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open && unread > 0) {
      api.put('/notifications/read-all').then(() => {
        setUnread(0);
        setNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
      }).catch(() => {});
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className={styles.wrap} ref={ref}>
      <button className={styles.bell} onClick={handleOpen} aria-label="Notifications">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <span className={styles.headerTitle}>Notifications</span>
            {unread === 0 && notifs.length > 0 && (
              <span className={styles.allRead}>All read</span>
            )}
          </div>

          <div className={styles.list}>
            {notifs.length === 0 ? (
              <div className={styles.empty}>
                <span>🔔</span>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifs.map(n => (
                <div
                  key={n.id}
                  className={`${styles.item} ${!n.isRead ? styles.itemUnread : ''}`}
                  onClick={() => setOpen(false)}
                >
                  {n.link ? (
                    <Link to={n.link} className={styles.itemInner}>
                      <span className={styles.itemIcon}>{TYPE_ICON[n.type] || '🔔'}</span>
                      <div className={styles.itemContent}>
                        <p className={styles.itemTitle}>{n.title}</p>
                        <p className={styles.itemBody}>{n.body}</p>
                        <p className={styles.itemTime}>{timeAgo(n.createdAt)}</p>
                      </div>
                      {!n.isRead && <span className={styles.dot} />}
                    </Link>
                  ) : (
                    <div className={styles.itemInner}>
                      <span className={styles.itemIcon}>{TYPE_ICON[n.type] || '🔔'}</span>
                      <div className={styles.itemContent}>
                        <p className={styles.itemTitle}>{n.title}</p>
                        <p className={styles.itemBody}>{n.body}</p>
                        <p className={styles.itemTime}>{timeAgo(n.createdAt)}</p>
                      </div>
                      {!n.isRead && <span className={styles.dot} />}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
